import React from 'react'
import { message, Modal } from 'antd'
import { createJobExportFromApi } from './createJobExport'

/**
 * Tầng dùng chung cho mọi nút Export tạo export job.
 *
 * Giải quyết 3 việc:
 *  1. Hiện notice NGAY khi user bấm (trước khi request bay đi), thay vì im lặng.
 *  2. Chặn click trùng cùng một loại export trong lúc request chưa trả về.
 *  3. Khi request thất bại theo kiểu hạ tầng (timeout, 5xx, mất mạng), hướng user KIỂM TRA
 *     Danh sách Job trước khi thử lại — vì job có thể đã được tạo ở server, nhưng cũng có thể
 *     KHÔNG: 4 endpoint này còn nhánh trả thẳng nội dung CSV (`createJobExport.js:188`), và
 *     trên nhánh đó không có job nào. Vì không phân biệt được ở thời điểm lỗi, thông báo cố ý
 *     KHÔNG khẳng định "job đã được tạo" — nó bảo user tự kiểm rồi quyết định.
 *
 * Khóa in-flight đặt ở cấp module, KHÔNG dựa vào state của component: setState là bất
 * đồng bộ nên hai click liên tiếp trong cùng một batch render đều đọc được state cũ và
 * đều lọt qua. Map dưới đây được ghi đồng bộ ngay trong handler nên click thứ 2 chắc chắn
 * bị chặn — đúng kịch bản user sốt ruột bấm liên tiếp nhiều lần.
 *
 * Khóa theo TỪNG loại export (`key`), không khóa chéo: user vẫn export song song các loại
 * báo cáo khác nhau, chỉ không bấm trùng cùng một loại.
 *
 * Đang áp dụng cho 5 nút export có payload KHÔNG giới hạn `page`/`limit` (nhóm dữ liệu lớn):
 * Export Orders, modal chi tiết thống kê item, QC report (đều ở /admin), Thống kê cutoff,
 * và Báo cáo chi tiết log thao tác.
 *
 * 4 nút **tạo export job** còn lại (Tag Brands, Brands, Brands Rule, lô ship) chỉ export 1
 * trang dữ liệu nên chưa cần — đã kiểm cả 4 đều gửi `page`/`limit`.
 *
 * NGOÀI phạm vi ticket nhưng CÙNG nguyên nhân gốc: các nút export không đi qua job cũng dùng
 * chung 2 factory không có `timeout` — `getExportTrackingInfo` / `getExportListBoxes`
 * (`services/api/apiNextBox.js`) và các endpoint export ở `apiBrands.js`. Riêng
 * `ExportTrackingInfo` gửi payload KHÔNG có `page`/`limit`, tức cũng thuộc nhóm dữ liệu lớn.
 * Đừng đọc danh sách trên như "đã phủ hết nút export".
 */
const _inFlight = new Map()
const _listeners = new Set()

/**
 * `duration` của toast "đang tạo job": 0 = sống tới khi `hideNotice()` được gọi.
 *
 * Từng thử đặt 10s cho gọn, nhưng đó là trade-off SAI và đã đo được: request chạy tới
 * `EXPORT_JOB_TIMEOUT` = 60s, nên toast tắt ở 10s để lại **50 giây UI hoàn toàn im lặng**
 * trên mọi đường mà nút Export bị unmount — đóng modal thống kê (`ModalDetailStaticsItem`
 * render có điều kiện), đóng panel QC report (chỉ báo là `<Spin>` bên trong panel), hoặc
 * đổi route. Im lặng 50s đúng là điều kiện sinh ra hành vi mà ticket này tồn tại để chặn:
 * user tưởng hỏng nên F5 hoặc bấm lại.
 *
 * Toast là chỉ báo DUY NHẤT đi xuyên được route (antd render nó vào React root riêng trên
 * `document.body`, ngoài cây router). Nên nó phải sống đúng bằng vòng đời request.
 *
 * An toàn vì `hideNotice()` nằm ở ĐẦU `finally` và `_notify` đã được bọc try/catch từng
 * listener — không còn đường nào bỏ qua nó, nên toast không thể mồ côi. Trần trên là 60s
 * do timeout. Đánh đổi còn lại: `message` của antd không có nút đóng; nếu muốn user tự
 * đóng được thì phải chuyển sang `notification` (có `closeIcon`, đã có tiền lệ
 * `notification.destroy()` trong repo).
 */
const EXPORT_NOTICE_DURATION = 0

/**
 * Gọi mọi listener, cô lập lỗi của từng cái.
 *
 * Vì sao cần try/catch: `_notify()` chạy cả trong `finally` của `runExportJob`. Nếu một
 * listener throw ở đó thì lỗi vượt ra khỏi `finally` và `runExportJob` REJECT — phá hợp
 * đồng "không bao giờ reject" mà các call site đang dựa vào (`UsersStaticsDetailPage`
 * gọi không có try/catch -> unhandled rejection). Ngoài ra một listener lỗi cũng không
 * được phép chặn các listener còn lại nhận thông báo.
 */
const _notify = () => _listeners.forEach(fn => {
    try {
        fn()
    } catch (e) {
        console.error('subscribeExporting listener failed', e)
    }
})

export const isExporting = (key) => _inFlight.has(key)

/**
 * Cho component quan sát khoá in-flight. Cần vì khoá sống ở cấp module trong khi state
 * `exporting` sống trong component: component bị unmount rồi mount lại (vd ModalDetailStaticsItem
 * render có điều kiện `{isOpen && ...}`) sẽ mất state và nút Export trông như sẵn sàng dù
 * request cũ còn đang bay.
 *
 * @param fn - gọi mỗi khi khoá thay đổi
 * @returns hàm unsubscribe
 */
export const subscribeExporting = (fn) => {
    _listeners.add(fn)

    return () => _listeners.delete(fn)
}

/**
 * Lỗi mà server CÓ THỂ đã nhận request và đã tạo job -> tuyệt đối không mời user bấm lại.
 * Gồm: timeout/abort phía client, 5xx (gateway hoặc exporter chết sau khi đã nhận request),
 * và network error (axios error nhưng không có response).
 *
 * Ngược lại, lỗi NGHIỆP VỤ (`success: false` từ server, 4xx) là lỗi thật -> báo lỗi, cho thử lại.
 * Phân biệt theo hướng này an toàn hơn liệt kê status code, vì mặc định của mọi lỗi hạ tầng
 * đều là "job có thể đã tồn tại".
 */
/**
 * Message có phải do SERVER viết không (thay vì nội bộ axios)?
 *
 * Interceptor của project (`services/request/packing.js`, `services/api/makeApiRequest.js`)
 * chỉ bọc lỗi khi response có body JSON, và khi bọc thì gắn `status`. Nên `e.status` có mặt
 * = server đã trả một message đọc được. Timeout / network error / gateway trả HTML thì không
 * có `status`, và `e.message` khi đó là chuỗi kỹ thuật của axios.
 */
const _hasServerMessage = (e) => !!(e && e.message && e.status)

const _mayHaveCreatedJob = (e) => {
    if (!e) return false

    // axios 0.19 gắn code 'ECONNABORTED' cho cả timeout và abort (xem lib/adapters/xhr.js)
    if (e.code === 'ECONNABORTED') return true

    // Interceptor của project bọc lỗi có response JSON thành plain object kèm `status`;
    // lỗi có response nhưng body không phải JSON (vd gateway trả HTML) giữ nguyên axios error.
    const status = e.status || (e.response && e.response.status) || 0
    if (status >= 500) return true

    // axios error không có response = không tới được server, hoặc mất kết nối giữa đường
    if (e.isAxiosError && !status) return true

    return false
}

/**
 * @param key       - Định danh loại export, vd 'list-items:export-orders'
 * @param callApi   - Hàm trả về promise của request tạo job (chưa gọi)
 * @param options   - { filename } dùng cho nhánh server trả thẳng nội dung CSV
 * @returns {Promise<void>}
 */
export const runExportJob = async (key, callApi, options = {}) => {
    if (_inFlight.has(key)) {
        // `key` để nhiều lần bấm liên tiếp THAY THẾ toast cũ thay vì xếp chồng. Không có nó,
        // mỗi click bị chặn lại thêm một notice mới (đo được 7 toast sau 6 click), phủ kín
        // dải trên màn hình và nằm trên cả Modal (z-index 1010 vs 1000).
        message.warning({
            content: 'Job export đang được tạo, vui lòng chờ.',
            key: `export-job-busy:${key}`,
        })
        return
    }
    _inFlight.set(key, true)

    // `hideNotice` phải khai báo TRƯỚC try để `finally` luôn thấy nó, kể cả khi
    // `_notify()` hoặc `message.loading` throw.
    let hideNotice = null

    try {
        _notify()

        // Notice tức thì. `duration` hữu hạn thay vì 0: toast của antd render vào React root
        // riêng trên body, KHÔNG thuộc cây component nào, nên `duration: 0` khiến nó sống sót
        // qua cả route change và treo trên mọi trang tới khi request settle (tối đa 60s) mà
        // không có nút đóng. Nhịp chặn spam do nút Export bị disable đảm nhiệm, không phải toast.
        // `key` riêng theo loại export: bấm lại cùng loại thì thay thế chứ không xếp chồng
        // (antd dùng `args.key` làm id notice trong `lib/message/index.js`), và `hideNotice()`
        // vẫn remove đúng notice đó.
        hideNotice = message.loading({
            content: 'Đang tạo job export, vui lòng chờ và không bấm lại...',
            duration: EXPORT_NOTICE_DURATION,
            key: `export-job:${key}`,
        })

        // createJobExportFromApi tự phân nhánh: có job -> mở Job modal, trả CSV thẳng -> tải luôn
        await createJobExportFromApi(callApi(), options.filename)
    } catch (e) {
        if (_mayHaveCreatedJob(e)) {
            Modal.warning({
                title: 'Hệ thống đang xử lý chậm',
                content: (
                    <div>
                        Yêu cầu export đã quá thời gian chờ. <strong>Kiểm tra{' '}
                        <a href="/jobs" target="_blank" rel="noopener noreferrer">Danh sách Job</a>{' '}
                        trước khi thử lại</strong>: nếu job đã được tạo, nó sẽ xuất hiện ở đó và bạn
                        chỉ cần chờ. Nếu không thấy job nào, bạn có thể export lại — cân nhắc thu hẹp
                        khoảng thời gian hoặc bộ lọc để dữ liệu nhỏ hơn.
                        {/* FAC-24827: giữ lại message của SERVER (vd 500 kèm "Filter quá lớn")
                            để user tự sửa được filter. Chỉ in khi `e.status` có mặt — đó là
                            dấu hiệu lỗi đã đi qua interceptor với body JSON, tức message do
                            server viết. Bỏ với timeout / network error, vì message ở đó là
                            nội bộ axios ("timeout of 60000ms exceeded", "Network Error",
                            "Request failed with status code 502") — vô nghĩa với nhân viên
                            kho và làm loãng thông điệp chính. */}
                        {_hasServerMessage(e) && (
                            <div className="mt-2 text-muted">Chi tiết: {e.message}</div>
                        )}
                    </div>
                ),
                okText: 'Đã hiểu',
            })
        } else {
            message.error((e && e.message) || 'Tạo job export thất bại.')
        }
    } finally {
        // Thứ tự có chủ đích: tắt toast TRƯỚC, rồi mới nhả khoá và notify.
        // Nếu một listener của `_notify()` throw thì toast đã tắt rồi, không treo vĩnh viễn.
        if (hideNotice) hideNotice()
        _inFlight.delete(key)
        _notify()
    }
}
